import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | null;
  hint?: string;
  leftIcon?: ReactNode;
  rightSlot?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, leftIcon, rightSlot, className = '', id, ...rest },
  ref,
) {
  const inputId = id ?? rest.name;
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="block text-[11px] font-black uppercase tracking-widest text-brand-muted">
          {label}
          {rest.required && <span aria-hidden="true" className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          className={[
            'w-full bg-white border rounded-xl px-3 py-2.5 text-sm font-medium text-brand-primary',
            'placeholder:text-gray-400 outline-none transition-all',
            'focus:ring-2 focus:ring-brand-accent focus:border-transparent',
            'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
            leftIcon ? 'pl-9' : '',
            rightSlot ? 'pr-10' : '',
            error ? 'border-red-300' : 'border-gray-200',
            className,
          ].join(' ')}
          {...rest}
        />
        {rightSlot && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2">{rightSlot}</span>
        )}
      </div>
      {error && <p className="text-xs text-red-700 font-medium">{error}</p>}
      {!error && hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
});

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string | null;
  hint?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, hint, className = '', id, ...rest },
  ref,
) {
  const textareaId = id ?? rest.name;
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={textareaId} className="block text-[11px] font-black uppercase tracking-widest text-brand-muted">
          {label}
          {rest.required && <span aria-hidden="true" className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        id={textareaId}
        className={[
          'w-full bg-white border rounded-xl px-3 py-2.5 text-sm font-medium text-brand-primary',
          'placeholder:text-gray-400 outline-none transition-all resize-y min-h-[80px]',
          'focus:ring-2 focus:ring-brand-accent focus:border-transparent',
          'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
          error ? 'border-red-300' : 'border-gray-200',
          className,
        ].join(' ')}
        {...rest}
      />
      {error && <p className="text-xs text-red-700 font-medium">{error}</p>}
      {!error && hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string | null;
  hint?: string;
  children: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, hint, className = '', id, children, ...rest },
  ref,
) {
  const selectId = id ?? rest.name;
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={selectId} className="block text-[11px] font-black uppercase tracking-widest text-brand-muted">
          {label}
          {rest.required && <span aria-hidden="true" className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <select
        ref={ref}
        id={selectId}
        className={[
          'w-full bg-white border rounded-xl px-3 py-2.5 text-sm font-medium text-brand-primary',
          'outline-none transition-all',
          'focus:ring-2 focus:ring-brand-accent focus:border-transparent',
          'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
          error ? 'border-red-300' : 'border-gray-200',
          className,
        ].join(' ')}
        {...rest}
      >
        {children}
      </select>
      {error && <p className="text-xs text-red-700 font-medium">{error}</p>}
      {!error && hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
});
